/**
 * Users Page - User management (Mobile Responsive)
 * Supports both global admins and org admins
 */
import { useState, useEffect } from 'react';
import { api, type User, type OrganizationMember } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';

type TabType = 'members' | 'invites';

interface Invite {
  id: string;
  token: string;
  link: string;
  defaultRole: string;
  maxUses: number | null;
  useCount: number;
  expiresAt: string | null;
  isExpired: boolean;
  isRevoked: boolean;
  isMaxUsesReached: boolean;
  createdAt: string;
  createdBy: { id: string; username: string };
}

export function UsersPage() {
  const { isGlobalAdmin, currentOrganizationId } = useAuth();
  const [activeTab, setActiveTab] = useState<TabType>('members');
  const [users, setUsers] = useState<User[]>([]);
  const [orgMembers, setOrgMembers] = useState<OrganizationMember[]>([]);
  const [invites, setInvites] = useState<Invite[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newUser, setNewUser] = useState({
    username: '',
    email: '',
    password: '',
    displayName: '',
    role: 'user' as 'admin' | 'user',
    orgRole: 'member' as 'member' | 'admin',
  });
  const [createError, setCreateError] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  // Invite modal
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteConfig, setInviteConfig] = useState({
    expiresIn: '7d' as '24h' | '7d' | '30d' | 'never',
    maxUses: 10 as number | null,
    defaultRole: 'member' as 'member' | 'admin',
  });
  const [isCreatingInvite, setIsCreatingInvite] = useState(false);
  const [inviteError, setInviteError] = useState('');
  const [generatedInviteLink, setGeneratedInviteLink] = useState('');
  const [copiedInviteId, setCopiedInviteId] = useState<string | null>(null);

  // Reset password modal
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [resetPasswordUser, setResetPasswordUser] = useState<User | null>(null);
  const [resetNewPassword, setResetNewPassword] = useState('');
  const [resetError, setResetError] = useState('');
  const [isResetting, setIsResetting] = useState(false);

  const loadUsers = async () => {
    setIsLoading(true);
    try {
      if (isGlobalAdmin) {
        // Global admins see all users
        const result = await api.getUsers();
        if (result.success && result.data) {
          setUsers(result.data.users);
        }
      } else if (currentOrganizationId) {
        // Org admins see organization members
        const result = await api.getOrganizationMembers(currentOrganizationId);
        if (result.success && result.data) {
          setOrgMembers(result.data.members);
        }
      }
    } catch (error) {
      console.error('Failed to load users:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadInvites = async () => {
    if (!currentOrganizationId) return;
    try {
      const result = await api.getInvites(currentOrganizationId);
      if (result.success && result.data) {
        setInvites(result.data.invites);
      }
    } catch (error) {
      console.error('Failed to load invites:', error);
    }
  };

  useEffect(() => {
    loadUsers();
    if (!isGlobalAdmin && currentOrganizationId) {
      loadInvites();
    }
  }, [isGlobalAdmin, currentOrganizationId]);

  // Listen for organization changes
  useEffect(() => {
    const handleOrgChange = () => {
      loadUsers();
      if (!isGlobalAdmin) {
        loadInvites();
      }
    };
    window.addEventListener('organizationChanged', handleOrgChange);
    return () => window.removeEventListener('organizationChanged', handleOrgChange);
  }, [isGlobalAdmin]);

  const handleCreateUser = async () => {
    if (!newUser.username || !newUser.password) {
      setCreateError('Username and password are required');
      return;
    }

    if (newUser.password.length < 6) {
      setCreateError('Password must be at least 6 characters');
      return;
    }

    setIsCreating(true);
    setCreateError('');

    try {
      if (isGlobalAdmin) {
        // Global admin creates global user
        const result = await api.createUser({
          username: newUser.username,
          password: newUser.password,
          role: newUser.role,
        });

        if (result.success) {
          setShowCreateModal(false);
          setNewUser({ username: '', email: '', password: '', displayName: '', role: 'user', orgRole: 'member' });
          loadUsers();
        } else {
          setCreateError(result.error?.message || 'Failed to create user');
        }
      } else if (currentOrganizationId) {
        // Org admin creates user for their organization
        const result = await api.createOrganizationUser(currentOrganizationId, {
          username: newUser.username,
          email: newUser.email || undefined,
          password: newUser.password,
          displayName: newUser.displayName || undefined,
          role: newUser.orgRole,
        });

        if (result.success) {
          setShowCreateModal(false);
          setNewUser({ username: '', email: '', password: '', displayName: '', role: 'user', orgRole: 'member' });
          loadUsers();
        } else {
          setCreateError(result.error?.message || 'Failed to create user');
        }
      }
    } catch (error) {
      setCreateError('An unexpected error occurred');
    }

    setIsCreating(false);
  };

  const handleToggleActive = async (user: User) => {
    const result = await api.updateUser(user.id, { isActive: !user.isActive });
    if (result.success) {
      loadUsers();
    }
  };

  const handleChangeRole = async (user: User, newRole: 'admin' | 'user') => {
    const result = await api.updateUser(user.id, { role: newRole });
    if (result.success) {
      loadUsers();
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const handleOpenResetPassword = (user: User) => {
    setResetPasswordUser(user);
    setResetNewPassword('');
    setResetError('');
    setShowResetPasswordModal(true);
  };

  const handleResetPassword = async () => {
    if (!resetPasswordUser) return;

    if (!resetNewPassword || resetNewPassword.length < 6) {
      setResetError('Password must be at least 6 characters');
      return;
    }

    setIsResetting(true);
    setResetError('');

    const result = await api.resetUserPassword(resetPasswordUser.id, resetNewPassword);

    if (result.success) {
      setShowResetPasswordModal(false);
      setResetPasswordUser(null);
      setResetNewPassword('');
      alert('Password reset successfully');
    } else {
      setResetError(result.error?.message || 'Failed to reset password');
    }

    setIsResetting(false);
  };

  // Invite management functions
  const handleCreateInvite = async () => {
    if (!currentOrganizationId) return;

    setIsCreatingInvite(true);
    setInviteError('');
    setGeneratedInviteLink('');

    try {
      const result = await api.createInvite(currentOrganizationId, inviteConfig);
      if (result.success && result.data) {
        setGeneratedInviteLink(result.data.link);
        loadInvites();
      } else {
        setInviteError(result.error?.message || 'Failed to create invite');
      }
    } catch (error) {
      setInviteError('An unexpected error occurred');
    }

    setIsCreatingInvite(false);
  };

  const handleCopyInviteLink = async (invite: Invite) => {
    try {
      await navigator.clipboard.writeText(invite.link);
      setCopiedInviteId(invite.id);
      setTimeout(() => setCopiedInviteId(null), 2000);
    } catch (error) {
      console.error('Failed to copy link:', error);
    }
  };

  const handleRevokeInvite = async (invite: Invite) => {
    if (!currentOrganizationId) return;
    if (!confirm('Are you sure you want to revoke this invite link?')) return;

    const result = await api.revokeInvite(currentOrganizationId, invite.id);
    if (result.success) {
      loadInvites();
    } else {
      alert(result.error?.message || 'Failed to revoke invite');
    }
  };

  const getInviteStatus = (invite: Invite) => {
    if (invite.isRevoked) return { label: 'Revoked', color: 'bg-red-100 text-red-800' };
    if (invite.isExpired) return { label: 'Expired', color: 'bg-gray-100 text-gray-800' };
    if (invite.isMaxUsesReached) return { label: 'Max Uses', color: 'bg-yellow-100 text-yellow-800' };
    return { label: 'Active', color: 'bg-green-100 text-green-800' };
  };

  // Render invites list
  const renderInvitesView = () => (
    <>
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {invites.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            No invite links created yet. Click "Create Invite" to generate one.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[600px]">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Link</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Uses</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {invites.map((invite) => {
                  const status = getInviteStatus(invite);
                  const isActive = !invite.isRevoked && !invite.isExpired && !invite.isMaxUsesReached;
                  return (
                    <tr key={invite.id} className="hover:bg-gray-50">
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-2">
                          <code className="text-xs bg-gray-100 px-2 py-1 rounded truncate max-w-[200px]">
                            {invite.link}
                          </code>
                          {isActive && (
                            <button
                              onClick={() => handleCopyInviteLink(invite)}
                              className="text-blue-600 hover:text-blue-800 text-sm"
                              title="Copy link"
                            >
                              {copiedInviteId === invite.id ? 'Copied!' : 'Copy'}
                            </button>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-4 capitalize">{invite.defaultRole}</td>
                      <td className="px-4 py-4">
                        {invite.useCount}{invite.maxUses ? `/${invite.maxUses}` : ''}
                      </td>
                      <td className="px-4 py-4">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${status.color}`}>
                          {status.label}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-gray-500 text-sm">
                        <div>{formatDate(invite.createdAt)}</div>
                        <div className="text-xs">by {invite.createdBy.username}</div>
                      </td>
                      <td className="px-4 py-4 text-right">
                        {isActive && (
                          <button
                            onClick={() => handleRevokeInvite(invite)}
                            className="text-red-600 hover:text-red-800 text-sm"
                          >
                            Revoke
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );

  // Render org members view for org admins
  const renderOrgMembersView = () => (
    <>
      {/* Mobile Card View */}
      <div className="sm:hidden space-y-3">
        {isLoading ? (
          <div className="bg-white rounded-lg shadow p-6 text-center text-gray-500">Loading...</div>
        ) : orgMembers.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-6 text-center text-gray-500">No members found</div>
        ) : (
          orgMembers.map((member) => (
            <div key={member.userId} className="bg-white rounded-lg shadow p-4">
              <div className="flex justify-between items-start mb-3">
                <div>
                  <div className="font-medium text-gray-900">{member.username}</div>
                  <div className="text-sm text-gray-500">{formatDate(member.createdAt)}</div>
                </div>
                <span
                  className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                    member.role === 'owner'
                      ? 'bg-purple-100 text-purple-800'
                      : member.role === 'admin'
                      ? 'bg-blue-100 text-blue-800'
                      : 'bg-gray-100 text-gray-800'
                  }`}
                >
                  {member.role.charAt(0).toUpperCase() + member.role.slice(1)}
                </span>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Desktop Table View */}
      <div className="hidden sm:block bg-white rounded-lg shadow overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-gray-500">Loading...</div>
        ) : orgMembers.length === 0 ? (
          <div className="p-8 text-center text-gray-500">No members found</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[500px]">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Username
                  </th>
                  <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Role
                  </th>
                  <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Joined
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {orgMembers.map((member) => (
                  <tr key={member.userId} className="hover:bg-gray-50">
                    <td className="px-4 lg:px-6 py-4 font-medium text-gray-900">
                      {member.username}
                    </td>
                    <td className="px-4 lg:px-6 py-4">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          member.role === 'owner'
                            ? 'bg-purple-100 text-purple-800'
                            : member.role === 'admin'
                            ? 'bg-blue-100 text-blue-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {member.role.charAt(0).toUpperCase() + member.role.slice(1)}
                      </span>
                    </td>
                    <td className="px-4 lg:px-6 py-4 text-gray-500 whitespace-nowrap">
                      {formatDate(member.createdAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );

  // Render global users view for global admins
  const renderGlobalUsersView = () => (
    <>
      {/* Mobile Card View */}
      <div className="sm:hidden space-y-3">
        {isLoading ? (
          <div className="bg-white rounded-lg shadow p-6 text-center text-gray-500">Loading...</div>
        ) : users.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-6 text-center text-gray-500">No users found</div>
        ) : (
          users.map((user) => (
            <div key={user.id} className="bg-white rounded-lg shadow p-4">
              <div className="flex justify-between items-start mb-3">
                <div>
                  <div className="font-medium text-gray-900">{user.username}</div>
                  <div className="text-sm text-gray-500">{formatDate(user.createdAt)}</div>
                </div>
                <span
                  className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                    user.isActive
                      ? 'bg-green-100 text-green-800'
                      : 'bg-red-100 text-red-800'
                  }`}
                >
                  {user.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
              <div className="flex items-center justify-between pt-3 border-t gap-2">
                <select
                  value={user.role}
                  onChange={(e) =>
                    handleChangeRole(user, e.target.value as 'admin' | 'user')
                  }
                  className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                >
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                </select>
                <div className="flex gap-3">
                  <button
                    onClick={() => handleOpenResetPassword(user)}
                    className="text-sm font-medium text-blue-600 hover:text-blue-800"
                  >
                    Reset Pwd
                  </button>
                  <button
                    onClick={() => handleToggleActive(user)}
                    className={`text-sm font-medium ${
                      user.isActive
                        ? 'text-red-600 hover:text-red-800'
                        : 'text-green-600 hover:text-green-800'
                    }`}
                  >
                    {user.isActive ? 'Deactivate' : 'Activate'}
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Desktop Table View */}
      <div className="hidden sm:block bg-white rounded-lg shadow overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-gray-500">Loading...</div>
        ) : users.length === 0 ? (
          <div className="p-8 text-center text-gray-500">No users found</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[600px]">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Username
                  </th>
                  <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Role
                  </th>
                  <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-4 lg:px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {users.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td className="px-4 lg:px-6 py-4 font-medium text-gray-900">
                      {user.username}
                    </td>
                    <td className="px-4 lg:px-6 py-4">
                      <select
                        value={user.role}
                        onChange={(e) =>
                          handleChangeRole(user, e.target.value as 'admin' | 'user')
                        }
                        className="px-3 py-1 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                      >
                        <option value="user">User</option>
                        <option value="admin">Admin</option>
                      </select>
                    </td>
                    <td className="px-4 lg:px-6 py-4">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          user.isActive
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {user.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 lg:px-6 py-4 text-gray-500 whitespace-nowrap">
                      {formatDate(user.createdAt)}
                    </td>
                    <td className="px-4 lg:px-6 py-4 text-right whitespace-nowrap">
                      <button
                        onClick={() => handleOpenResetPassword(user)}
                        className="text-sm text-blue-600 hover:text-blue-800 mr-3"
                      >
                        Reset Password
                      </button>
                      <button
                        onClick={() => handleToggleActive(user)}
                        className={`text-sm ${
                          user.isActive
                            ? 'text-red-600 hover:text-red-800'
                            : 'text-green-600 hover:text-green-800'
                        }`}
                      >
                        {user.isActive ? 'Deactivate' : 'Activate'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      <div className="flex items-center justify-between mb-4 sm:mb-6">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
          {isGlobalAdmin ? 'User Management' : 'Organization Members'}
        </h1>
        <div className="flex gap-2">
          {/* For org admins, show different buttons based on active tab */}
          {!isGlobalAdmin && activeTab === 'invites' ? (
            <button
              onClick={() => {
                setShowInviteModal(true);
                setGeneratedInviteLink('');
                setInviteError('');
              }}
              className="bg-blue-600 text-white px-3 sm:px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm sm:text-base"
            >
              + Create Invite
            </button>
          ) : (
            <button
              onClick={() => setShowCreateModal(true)}
              className="bg-blue-600 text-white px-3 sm:px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm sm:text-base"
            >
              + Add User
            </button>
          )}
        </div>
      </div>

      {/* Tabs for org admins */}
      {!isGlobalAdmin && (
        <div className="flex gap-2 mb-4 border-b">
          <button
            onClick={() => setActiveTab('members')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'members'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Members
          </button>
          <button
            onClick={() => setActiveTab('invites')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'invites'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Invite Links
          </button>
        </div>
      )}

      {/* Show appropriate view based on user type and tab */}
      {isGlobalAdmin
        ? renderGlobalUsersView()
        : activeTab === 'members'
        ? renderOrgMembersView()
        : renderInvitesView()}

      {/* Create User Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-4 sm:p-6">
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-4">
              {isGlobalAdmin ? 'Create New User' : 'Add Organization Member'}
            </h2>

            {createError && (
              <div className="bg-red-50 text-red-600 p-3 rounded-lg mb-4 text-sm">
                {createError}
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Username *
                </label>
                <input
                  type="text"
                  value={newUser.username}
                  onChange={(e) =>
                    setNewUser({ ...newUser, username: e.target.value })
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  placeholder="Enter username"
                />
              </div>

              {/* Email field for org admin user creation */}
              {!isGlobalAdmin && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    value={newUser.email}
                    onChange={(e) =>
                      setNewUser({ ...newUser, email: e.target.value })
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    placeholder="Enter email (optional)"
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Password *
                </label>
                <input
                  type="password"
                  value={newUser.password}
                  onChange={(e) =>
                    setNewUser({ ...newUser, password: e.target.value })
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  placeholder="Enter password (min 6 characters)"
                />
              </div>

              {/* Display name for org admin user creation */}
              {!isGlobalAdmin && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Display Name
                  </label>
                  <input
                    type="text"
                    value={newUser.displayName}
                    onChange={(e) =>
                      setNewUser({ ...newUser, displayName: e.target.value })
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    placeholder="Enter display name (optional)"
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {isGlobalAdmin ? 'Global Role' : 'Organization Role'}
                </label>
                {isGlobalAdmin ? (
                  <select
                    value={newUser.role}
                    onChange={(e) =>
                      setNewUser({
                        ...newUser,
                        role: e.target.value as 'admin' | 'user',
                      })
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  >
                    <option value="user">User</option>
                    <option value="admin">Admin</option>
                  </select>
                ) : (
                  <select
                    value={newUser.orgRole}
                    onChange={(e) =>
                      setNewUser({
                        ...newUser,
                        orgRole: e.target.value as 'member' | 'admin',
                      })
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  >
                    <option value="member">Member</option>
                    <option value="admin">Admin</option>
                  </select>
                )}
              </div>
            </div>

            <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 sm:gap-3 mt-6">
              <button
                onClick={() => setShowCreateModal(false)}
                className="w-full sm:w-auto px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateUser}
                disabled={isCreating}
                className="w-full sm:w-auto px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
              >
                {isCreating ? 'Creating...' : 'Create User'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {showResetPasswordModal && resetPasswordUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-4 sm:p-6">
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-4">
              Reset Password for {resetPasswordUser.username}
            </h2>

            {resetError && (
              <div className="bg-red-50 text-red-600 p-3 rounded-lg mb-4 text-sm">
                {resetError}
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  New Password
                </label>
                <input
                  type="password"
                  value={resetNewPassword}
                  onChange={(e) => setResetNewPassword(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  placeholder="Enter new password (min 6 characters)"
                />
              </div>
            </div>

            <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 sm:gap-3 mt-6">
              <button
                onClick={() => {
                  setShowResetPasswordModal(false);
                  setResetPasswordUser(null);
                }}
                className="w-full sm:w-auto px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleResetPassword}
                disabled={isResetting}
                className="w-full sm:w-auto px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
              >
                {isResetting ? 'Resetting...' : 'Reset Password'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Invite Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-4 sm:p-6">
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-4">
              Create Invite Link
            </h2>

            {inviteError && (
              <div className="bg-red-50 text-red-600 p-3 rounded-lg mb-4 text-sm">
                {inviteError}
              </div>
            )}

            {generatedInviteLink ? (
              <div className="space-y-4">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <p className="text-sm text-green-800 mb-2 font-medium">Invite link created!</p>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={generatedInviteLink}
                      readOnly
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
                    />
                    <button
                      onClick={async () => {
                        await navigator.clipboard.writeText(generatedInviteLink);
                        alert('Link copied!');
                      }}
                      className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
                    >
                      Copy
                    </button>
                  </div>
                </div>
                <div className="flex justify-end">
                  <button
                    onClick={() => setShowInviteModal(false)}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    Done
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Expires In
                    </label>
                    <select
                      value={inviteConfig.expiresIn}
                      onChange={(e) =>
                        setInviteConfig({
                          ...inviteConfig,
                          expiresIn: e.target.value as '24h' | '7d' | '30d' | 'never',
                        })
                      }
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    >
                      <option value="24h">24 hours</option>
                      <option value="7d">7 days</option>
                      <option value="30d">30 days</option>
                      <option value="never">Never</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Max Uses
                    </label>
                    <select
                      value={inviteConfig.maxUses === null ? 'unlimited' : inviteConfig.maxUses.toString()}
                      onChange={(e) =>
                        setInviteConfig({
                          ...inviteConfig,
                          maxUses: e.target.value === 'unlimited' ? null : parseInt(e.target.value),
                        })
                      }
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    >
                      <option value="1">1 use</option>
                      <option value="5">5 uses</option>
                      <option value="10">10 uses</option>
                      <option value="25">25 uses</option>
                      <option value="100">100 uses</option>
                      <option value="unlimited">Unlimited</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Default Role
                    </label>
                    <select
                      value={inviteConfig.defaultRole}
                      onChange={(e) =>
                        setInviteConfig({
                          ...inviteConfig,
                          defaultRole: e.target.value as 'member' | 'admin',
                        })
                      }
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    >
                      <option value="member">Member</option>
                      <option value="admin">Admin</option>
                    </select>
                  </div>
                </div>

                <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 sm:gap-3 mt-6">
                  <button
                    onClick={() => setShowInviteModal(false)}
                    className="w-full sm:w-auto px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCreateInvite}
                    disabled={isCreatingInvite}
                    className="w-full sm:w-auto px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
                  >
                    {isCreatingInvite ? 'Creating...' : 'Create Invite'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
